import { defaultKeys } from '@/constants/keys'
import FireosPlatform from '@/platform/fireos'
import { platformName } from '@/platform/platformUtils'
import TizenPlatform from '@/platform/tizen'
import WebosPlatform from '@/platform/webos'
import { ChannelType, DeepLinkType } from '@/types/servicesTypes/configTypes'
import { HasPromotionType } from '@/types/utilsTypes'
import { showInfo } from '@/utils/utils'

const userAgent = window.navigator.userAgent

export const platformManufacturer = (() => {
  const manufacturers = [
    { name: 'Samsung', keyword: 'Tizen' },
    { name: 'LG', keyword: 'Web0S' },
    { name: 'Amazon', keyword: ['AFT', 'AmazonWebAppPlatform'] },
    { name: 'Unknown', keyword: ['Linux', 'Win', 'X11'] },
    { name: 'Apple', keyword: 'Mac' },
  ]

  for (const manufacturer of manufacturers) {
    if (Array.isArray(manufacturer.keyword)) {
      if (manufacturer.keyword.some((value) => userAgent.includes(value))) {
        return manufacturer.name
      }
    } else if (userAgent.includes(manufacturer.keyword)) {
      return manufacturer.name
    }
  }
  return ''
})()

export const getPlatformName = () => platformName.toLocaleLowerCase()

const detectPlatform = () => {
  const type = getPlatformName()
  switch (type) {
    case 'tizen':
      return new TizenPlatform()
    case 'webos':
      return new WebosPlatform()
    case 'fireos':
      return new FireosPlatform()
    default:
      break
  }
}

const notSupported = () => {
  return 'not supported'
}

const platform = detectPlatform()

export const Keys = platform?.keys || defaultKeys

export const getDuid = () => platform?.getDuid() || notSupported()
export const getIfa = () => platform?.getIfa() || notSupported()
export const getLat = () => platform?.getLat() || false
export const getUuid = () => platform?.getUuid() || notSupported()
export const getModelCode = () => platform?.getModelCode() || notSupported()
export const getModelName = () => platform?.getModelName() || notSupported()
export const getModelYear = () => platform?.getModelYear() || notSupported()
export const getVersion = () => platform?.getVersion() || notSupported()
export const getWidgetVersion = () =>
  platform?.getWidgetVersion() || notSupported()
export const getTotalMemory = () => platform?.getTotalMemory()
export const getAvailableMemory = () => platform?.getAvailableMemory()
export const isChannelTunable = (channel: ChannelType) =>
  platform?.isChannelTunable(channel)
export const tuneToChannel = (channel: ChannelType) =>
  platform?.tuneToChannel(channel) || notSupported()
export const deepLinkToPromotion = (promotionData: HasPromotionType) =>
  platform?.deepLinkToPromotion(promotionData) || notSupported()
export const deepLinkToAppId = ({
  appId,
  appName,
  payload,
}: Exclude<DeepLinkType['tizen'], undefined>) =>
  platform?.deepLinkToAppId({
    appId,
    appName,
    payload,
  })
export const closeApp = () => platform?.closeApp() || notSupported()
export const reloadApp = () => {
  return (
    platform?.reloadApp() ||
    (showInfo('Browser detected, reloading...'),
    setTimeout(() => window.location.reload(), 1000))
  )
}
export const openAppStorePage = () =>
  platform?.openAppStorePage() || notSupported()
export const getAppBundleName = () => {
  return platform?.getAppBundleName() || notSupported()
}
export const getPlatformBase64 = () => {
  const modelCode = getModelCode()
  const model = getModelName()
  const year = modelCode ? `20${modelCode?.slice(0, 2)}` : notSupported()
  const appVersion = import.meta.env.REACT_APP_VERSION
  const platformObj = {
    manufacturer: platformManufacturer.toLocaleLowerCase(),
    type: 'tv',
    os: platformName.toLocaleLowerCase(),
    model,
    year,
    appVersion,
  }
  return btoa(JSON.stringify(platformObj))
}
